import { useState, useEffect, useRef } from "react";
import { ChevronDown, Search } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
  defaultValue?: string;
  value?: string;
  readOnly?: boolean;
  dropUp?: boolean;
  theme?: "light" | "dark";
  searchPlaceholder?: string;
  error?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  placeholder = "Selecione uma opção",
  onChange,
  className = "",
  defaultValue = "",
  value,
  readOnly = false,
  dropUp = false,
  theme,
  searchPlaceholder = "Pesquisar...",
  error = false,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(defaultValue);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filtrar opções com base no termo de pesquisa
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (value !== undefined) setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && selectRef.current && !selectRef.current.contains(event.target as Node)) {
        requestAnimationFrame(() => {
          setIsOpen(false);
          setSearchTerm("");
        });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Focar no campo de pesquisa quando o dropdown é aberto
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectClick = () => {
    if (!readOnly) setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionValue: string, event?: React.MouseEvent) => {
    if (readOnly) return;

    event?.stopPropagation();

    setSelectedValue(optionValue);
    onChange(optionValue);
    setSearchTerm("");

    requestAnimationFrame(() => {
      setIsOpen(false);
    });
  };

  const selectedLabel = selectedValue
    ? options.find(option => option.value === selectedValue)?.label || selectedValue
    : placeholder;

  // ==============================
  // CLASSES DINÂMICAS
  // ==============================
  let selectClasses = "h-11 w-full rounded-lg border px-4 py-2.5 pr-11 text-sm shadow-theme-xs cursor-pointer flex items-center justify-between focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 ";
  let labelColor = "";
  let chevronColor = "transition-transform duration-200 ";
  let dropdownClasses = "absolute z-10 w-full rounded-lg shadow-lg mt-1 ";

  // LIGHT MODE
  if (theme === "light") {
    selectClasses += "border-gray-200 bg-white ";
    selectClasses += selectedValue ? "text-gray-800 " : "text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedValue ? "text-gray-800" : "text-gray-400";
    chevronColor += "text-gray-500";
    dropdownClasses += "bg-white border border-gray-300 ";
  }
  // DARK MODE
  else if (theme === "dark") {
    selectClasses += "border-gray-700 bg-gray-900 ";
    selectClasses += selectedValue ? "text-white/90 " : "text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedValue ? "text-white/90" : "text-gray-400";
    chevronColor += "text-gray-400";
    dropdownClasses += "bg-gray-900 border border-gray-700 ";
  }
  // PADRÃO (herda dark: do tema global)
  else {
    selectClasses += "border-gray-200 bg-transparent dark:border-gray-800 dark:bg-gray-900 ";
    selectClasses += selectedValue ? "text-gray-800 dark:text-white/90 " : "text-gray-400 dark:text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedValue ? "text-gray-800 dark:text-white/90" : "text-gray-400 dark:text-gray-400";
    chevronColor += "text-gray-500 dark:text-gray-400";
    dropdownClasses += "bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 ";
  }

  // Erro
  if (error) {
    selectClasses += "border-error-500 ";
  }

  dropdownClasses += dropUp ? "bottom-full mb-1" : "top-full mt-1";

  return (
    <div className={`relative w-full ${className}`} ref={selectRef}>
      {/* Campo de seleção */}
      <div className={selectClasses} onClick={handleSelectClick}>
        <span className={labelColor}>{selectedLabel}</span>
        <ChevronDown
          size={18}
          className={`${chevronColor} ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </div>
      {/* Dropdown */}
      {isOpen && (
        <div className={dropdownClasses}>
          {/* Campo de pesquisa */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full rounded border border-gray-200 py-2 pl-10 pr-4 text-sm outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-800 dark:text-white/90"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Lista de opções */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                Nenhuma opção encontrada
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${theme === "dark"
                    ? "text-white/90 hover:bg-gray-800"
                    : theme === "light"
                      ? "text-gray-700"
                      : "text-gray-700 dark:text-white/90 dark:hover:bg-gray-800"
                    } ${selectedValue === option.value ? "bg-gray-100 dark:bg-gray-800" : ""}`}
                  onClick={(event) => handleOptionClick(option.value, event)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
